import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore } from './socket';
import { playSound } from '../utils/alarmSounds';

export interface Reminder {
  id: string;
  userId: string;
  title: string;
  description: string | null;
  remindAt: string;
  recurrence: string | null;
  sound: string;
  completed: boolean;
  calendarEventId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReminderDue {
  reminderId: string;
  title: string;
  description: string | null;
  sound: string;
  calendarEventId: string | null;
}

export const useRemindersStore = defineStore('reminders', () => {
  const reminders = ref<Reminder[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const recentlyDue = ref<ReminderDue | null>(null);

  // ── Socket listeners ────────────────────────────────────────────────────────
  const socketStore = useSocketStore();

  function handleReminderDue(data: ReminderDue) {
    recentlyDue.value = data;
    playSound(data.sound).catch(console.error);
    // Mark as completed in local state
    const r = reminders.value.find((r) => r.id === data.reminderId);
    if (r) r.completed = true;
    // Auto-clear the toast after 10s
    setTimeout(() => {
      if (recentlyDue.value?.reminderId === data.reminderId) {
        recentlyDue.value = null;
      }
    }, 10_000);
  }

  function _registerSocketListeners() {
    const s = socketStore.socket;
    if (!s) return;
    s.off('reminder:due', handleReminderDue);
    s.on('reminder:due', handleReminderDue);
  }

  function initSocketListeners() {
    _registerSocketListeners();
    watch(
      () => socketStore.socket,
      (s) => {
        if (s) _registerSocketListeners();
      },
    );
  }

  // ── API ────────────────────────────────────────────────────────────────────

  async function fetchReminders(filters: { completed?: boolean; upcoming?: boolean } = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = new URLSearchParams();
      if (filters.completed !== undefined) params.set('completed', String(filters.completed));
      if (filters.upcoming) params.set('upcoming', 'true');
      const qs = params.toString();
      const data = await apiFetch(`/reminders${qs ? `?${qs}` : ''}`);
      if (filters.completed === true) {
        // Merge completed into existing list
        const ids = new Set(data.map((r: Reminder) => r.id));
        reminders.value = [...reminders.value.filter((r) => !ids.has(r.id)), ...data];
      } else {
        reminders.value = data;
      }
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to load reminders';
    } finally {
      loading.value = false;
    }
  }

  async function createReminder(payload: Partial<Reminder>): Promise<Reminder> {
    const reminder = await apiFetch('/reminders', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    reminders.value.push(reminder);
    reminders.value.sort((a, b) => new Date(a.remindAt).getTime() - new Date(b.remindAt).getTime());
    return reminder;
  }

  async function updateReminder(id: string, payload: Partial<Reminder>): Promise<Reminder> {
    const reminder = await apiFetch(`/reminders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const idx = reminders.value.findIndex((r) => r.id === id);
    if (idx !== -1) reminders.value[idx] = reminder;
    return reminder;
  }

  async function completeReminder(id: string): Promise<void> {
    const reminder = await apiFetch(`/reminders/${id}/complete`, { method: 'PATCH' });
    const idx = reminders.value.findIndex((r) => r.id === id);
    if (idx !== -1) reminders.value[idx] = reminder;
  }

  async function deleteReminder(id: string): Promise<void> {
    await apiFetch(`/reminders/${id}`, { method: 'DELETE' });
    reminders.value = reminders.value.filter((r) => r.id !== id);
  }

  function dismissDue() {
    recentlyDue.value = null;
  }

  return {
    reminders,
    loading,
    error,
    recentlyDue,
    initSocketListeners,
    fetchReminders,
    createReminder,
    updateReminder,
    completeReminder,
    deleteReminder,
    dismissDue,
  };
});
